import { UNPROCESSABLE_CONTENT_CODE } from '~/common/constants/errorCodes';
import { AuthMethods } from '~/components/AuthenticationProvider/constants';

import { RestApi } from '../restApi';
import { getErrorMessage } from '../utils';

import type {
  CreateUserRESTRequest,
  GetAuthTokenRESTResponse,
  GetPasswordResetEmailResponse,
  SocialLoginArgs,
  ValidateEmailResponse,
  GetAuthTokenRESTRequest,
  LoginSocialNewUserRestResponse,
  LoginSocialExistingUserRestResponse,
  SendPhoneVerificationCodeRESTResponse,
  Verify2FACodeRESTResponse,
  VerifyContactResponse,
} from './types';
import type { AxiosError, AxiosResponse } from 'axios';

export class AuthApi extends RestApi {
  static async createUser(data: CreateUserRESTRequest): Promise<GetAuthTokenRESTResponse> {
    try {
      const response: AxiosResponse<GetAuthTokenRESTResponse> = await this.woofApi.post(
        'v1/users',
        data
      );

      return { ...response.data };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      this.handleError(error as AxiosError);
      throw error.response.data.errors;
    }
  }

  static async getAuthToken(data: GetAuthTokenRESTRequest): Promise<GetAuthTokenRESTResponse> {
    try {
      const response: AxiosResponse<GetAuthTokenRESTResponse> = await this.woofApi.post(
        'v1/oauth/token',
        data
      );

      return { ...response.data };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      this.handleError(error as AxiosError);
      throw error.response.data.errors;
    }
  }

  static async getPasswordResetEmail(email: string): Promise<GetPasswordResetEmailResponse | null> {
    try {
      const response: AxiosResponse<GetPasswordResetEmailResponse> = await this.woofApi.post(
        'v1/password/email',
        {
          email,
          client_id: Number(process.env.NEXT_PUBLIC_CLIENT_ID ?? 0),
          client_secret: process.env.NEXT_PUBLIC_CLIENT_SECRET ?? '',
        }
      );

      return { ...response.data };
    } catch (error) {
      this.handleError(error as AxiosError);
      throw new Error(getErrorMessage(error as AxiosError));
    }
  }

  private static getSocialLoginFromAuthMethod(method: AuthMethods): string {
    switch (method) {
      case AuthMethods.FACEBOOK:
        return 'facebook';
      case AuthMethods.GOOGLE:
        return 'google';
      case AuthMethods.GOOGLE_ONE_TAP:
        return 'google';
      default:
        throw new Error(`Unsupported sign up method: ${method}`);
    }
  }

  static async logOut(): Promise<boolean> {
    try {
      await this.woofApi.delete('/v1/oauth/token');
      return true;
    } catch (error) {
      this.handleError(error as AxiosError);
      return false;
    }
  }

  static async loginWithSocials(
    args: SocialLoginArgs,
    authMethod: AuthMethods
  ): Promise<LoginSocialNewUserRestResponse | LoginSocialExistingUserRestResponse | null> {
    try {
      const response: AxiosResponse<
        LoginSocialNewUserRestResponse | LoginSocialExistingUserRestResponse
      > = await this.woofApi.post(
        `/v3/users/login_with_${this.getSocialLoginFromAuthMethod(authMethod)}`,
        { ...args }
      );

      return { ...response.data };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (
        error.response.status === UNPROCESSABLE_CONTENT_CODE &&
        error.response?.data?.userNeedsToAcceptTerms
      ) {
        return error.response.data;
      }

      return this.handleError(error as AxiosError);
    }
  }

  static async resetPassword(
    email: string,
    password: string,
    passwordConfirmation: string,
    token: string
  ): Promise<GetPasswordResetEmailResponse | null> {
    try {
      const response: AxiosResponse<GetPasswordResetEmailResponse> = await this.woofApi.post(
        'v1/password',
        {
          email,
          password,
          passwordConfirmation,
          token,
          grant_type: AuthMethods.PASSWORD,
          client_id: Number(process.env.NEXT_PUBLIC_CLIENT_ID ?? 0),
          client_secret: process.env.NEXT_PUBLIC_CLIENT_SECRET ?? '',
        }
      );

      return { ...response.data };
    } catch (error) {
      this.handleError(error as AxiosError);
      throw new Error(getErrorMessage(error as AxiosError));
    }
  }

  static async sendPhoneVerificationCode(contactNumber: string): Promise<string> {
    try {
      const response = await this.woofApi.post<SendPhoneVerificationCodeRESTResponse>(
        '/v1/phones/send_verification',
        {
          contactNumber,
          type: 'code',
        }
      );

      return response.data.message;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error) {
      this.handleError(error as AxiosError);
      throw error;
    }
  }

  static async validateEmail(email: string): Promise<ValidateEmailResponse | null> {
    try {
      const response: AxiosResponse<ValidateEmailResponse> = await this.woofApi.post(
        '/v3/users/validate_email',
        { email }
      );

      return { ...response.data };
    } catch (error) {
      return this.handleError(error as AxiosError);
    }
  }

  static async verify2FACode(data: {
    isNewUser: boolean;
    verificationCode: string;
  }): Promise<string> {
    try {
      const response = await this.woofApi.post<Verify2FACodeRESTResponse>('/v3/users/verify', {
        ...data,
      });

      return response.data.message;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      this.handleError(error as AxiosError);

      throw error;
    }
  }

  static async verifyContact(
    type: string,
    token: string,
    headers?: Record<string, string>
  ): Promise<VerifyContactResponse> {
    const verificationResource = {
      email: 'emails',
      emails: 'emails',
      phone: 'phones',
      phones: 'phones',
    }[type];

    if (!verificationResource) {
      throw new Error(`Unsupported verification type: ${type}`);
    }

    const response = await this.woofApi.post<VerifyContactResponse>(
      `/v1/${verificationResource}/verify`,
      { token },
      { headers }
    );

    return response.data;
  }
}
